// Customiser funnel analytics.
//
// Two things happen for every event:
//   1. A `rk-pc:<name>` DOM CustomEvent is dispatched on `document`, so
//      theme code or a future Klaviyo listener can subscribe without us
//      hard-coding it here.
//   2. The event is forwarded straight to the storefront's GA4 + Meta
//      Pixel. The customiser runs inline on the PDP, in the same window as
//      the theme, so `window.gtag` and `window.fbq` are already loaded.
//      Each forward is best-effort: if a tracker isn't on the page the
//      call is skipped silently.
//
// GA4 + Meta IDs are public (they ship in the storefront HTML anyway).
// We target them explicitly via `send_to` / `trackSingle` so the events
// land on RK Threads' own property even when other pixels share the page.

const GA4_MEASUREMENT_ID = "G-YRTJZXMQK0";
const META_PIXEL_ID = "818177685382946";

export type AnalyticsEventName =
  | "template_view"
  | "design_started"
  | "spot_saved"
  | "spot_removed"
  | "upsell_picked"
  | "proof_picked"
  // Starter examples (2026-07-19): a pre-filled example tile tap. Fires
  // alongside design_started so the funnel stays comparable; the event
  // itself tells us which starters convert.
  | "starter_picked"
  | "past_order_used"
  | "reuse_deeplink"
  | "open_deeplink"
  | "draft_resumed"
  | "add_to_cart_success"
  | "add_to_cart_failure"
  | "stuck_helper_shown"
  | "stuck_helper_sent"
  | "preorder_offered"
  | "preorder_requested"
  | "restock_requested"
  // Wave 1 (2026-07-11): step-level funnel instrumentation. Each fires at
  // most once per spot (placement/upload/threads/size) or once per
  // session (atc_shown), so the funnel reads as unique progress markers.
  | "placement_picked"
  | "upload_done"
  | "threads_done"
  | "size_done"
  | "atc_shown"
  | "atc_clicked"
  // Wave 3 (2026-07-12): the consolidated "Finish your order" screen.
  // finish_shown fires once, the first time the screen renders; the two
  // finish_addon_* events measure the paid order-add-on cards (gift box /
  // rush) - an impression once per addon_kind + one accept per pick.
  | "finish_shown"
  | "finish_addon_view"
  | "finish_addon_accept"
  // Upload-later (2026-07-17): the customer chose "I'll send it later"
  // instead of uploading artwork on the PDP. Fires per tap (an undo +
  // re-tap counts again - it's a decision event, not a progress marker).
  | "upload_later_chosen"
  // Pet-photo trap (2026-07-12): a logo-flow upload classified as a
  // photo, and the in-place Portrait upgrade offer's outcome. Detected
  // fires at most once per spot; taken/declined once per card choice.
  | "photo_detected"
  | "photo_upgrade_taken"
  | "photo_upgrade_declined"
  // W4 phase 2 (2026-07-12): live on-garment photo preview.
  // preview_photo_shown fires once per placement per mount (the dock
  // rendered photo mode for that placement); preview_dragged once per
  // spot, on the first real reposition drag.
  | "preview_photo_shown"
  | "preview_dragged";

export type AnalyticsEventDetail = {
  productGid?: string;
  productHandle?: string;
  variantId?: string | null;
  // spot_saved / spot_removed / upsell_picked context
  spotCount?: number;
  spotIndex?: number;
  placementSlug?: string | null;
  mode?: string | null;
  // upsell_picked context
  upsellId?: string;
  upsellKind?: string;
  upsellLabel?: string;
  upsellPriceCents?: number;
  // add_to_cart context
  totalCents?: number;
  currency?: string;
  approvalChoice?: string | null;
  approvalAddonId?: string | null;
  // template_view context
  templateName?: string;
  templateTag?: string;
  // Logged-in (or guest-captured) email. Used to identify the Klaviyo
  // profile so abandoned-design recovery can reach a real person. Only
  // set on design_started today.
  customerEmail?: string;
  // Wave 1 funnel context: which flow the template runs ("logo", "pet",
  // "kid", "person") and, on upload_done, how long the upload took.
  flowKind?: string;
  uploadMs?: number;
  // Wave 3: which paid order add-on a finish_addon_* event is about
  // (the upsell's `kind`, e.g. "gift_box" / "rush").
  addonKind?: string;
  // Pet-photo trap: total cents of the accepted Portrait upgrade
  // (base + coloured-and-filled surcharge). photo_upgrade_taken only.
  priceCents?: number;
  // Starter examples: the tapped tile's text + Word Art preset (if any).
  text?: string;
  wordArtStyleId?: string;
};

// Each rk event maps to one GA4 event name plus a Meta event. Meta gets a
// standard event where one fits the meaning (CustomizeProduct for a saved
// spot), otherwise a namespaced custom event.
const EVENT_MAP: Record<
  AnalyticsEventName,
  { ga4: string; metaStandard?: string; metaCustom?: string }
> = {
  template_view: { ga4: "rk_customiser_view", metaCustom: "RKCustomiserView" },
  design_started: { ga4: "rk_design_started", metaCustom: "RKDesignStarted" },
  spot_saved: { ga4: "rk_spot_saved", metaStandard: "CustomizeProduct" },
  spot_removed: { ga4: "rk_spot_removed", metaCustom: "RKSpotRemoved" },
  upsell_picked: { ga4: "rk_upsell_picked", metaCustom: "RKUpsellPicked" },
  proof_picked: { ga4: "rk_proof_picked", metaCustom: "RKProofPicked" },
  starter_picked: { ga4: "rk_starter_picked", metaCustom: "RKStarterPicked" },
  past_order_used: { ga4: "rk_past_order_used", metaCustom: "RKPastOrderUsed" },
  // 2026-07-02 CRO 2.5: a Klaviyo reorder email landed the customer on a
  // PDP via ?rk_reuse=<order>. Measures the post-delivery flow's clicks
  // through to the auto-fired past-order lookup.
  reuse_deeplink: { ga4: "rk_reuse_deeplink", metaCustom: "RKReuseDeepLink" },
  // 2026-07-22: the Father's Day collection hero ("One gift, sorted.") sent
  // the customer to the bundle PDP via ?rk_open=1 and the customiser eased
  // itself into view. Pair with rk_design_started to see whether the hero
  // actually converts a click into a design, or just into a scroll.
  open_deeplink: { ga4: "rk_open_deeplink", metaCustom: "RKOpenDeepLink" },
  // Abandonment recovery: a ?rk_draft deep link successfully restored a
  // server-saved design (state hydrated, editor rebuilt).
  draft_resumed: { ga4: "rk_draft_resumed", metaCustom: "RKDraftResumed" },
  add_to_cart_success: {
    ga4: "rk_customiser_add_to_cart",
    metaCustom: "RKCustomiserAddToCart",
  },
  add_to_cart_failure: {
    ga4: "rk_customiser_error",
    metaCustom: "RKCustomiserError",
  },
  // 2026-07-03: stuck-helper + sold-out rescue funnel.
  stuck_helper_shown: { ga4: "rk_stuck_helper_shown", metaCustom: "RKStuckHelperShown" },
  stuck_helper_sent: { ga4: "rk_stuck_helper_sent", metaStandard: "Lead" },
  preorder_offered: { ga4: "rk_preorder_offered", metaCustom: "RKPreorderOffered" },
  preorder_requested: { ga4: "rk_preorder_requested", metaStandard: "Lead" },
  restock_requested: { ga4: "rk_restock_requested", metaStandard: "Lead" },
  // Wave 1: step-level funnel markers.
  placement_picked: { ga4: "rk_placement_picked", metaCustom: "RKPlacementPicked" },
  upload_done: { ga4: "rk_upload_done", metaCustom: "RKUploadDone" },
  threads_done: { ga4: "rk_threads_done", metaCustom: "RKThreadsDone" },
  size_done: { ga4: "rk_size_done", metaCustom: "RKSizeDone" },
  atc_shown: { ga4: "rk_atc_shown", metaCustom: "RKAtcShown" },
  atc_clicked: { ga4: "rk_atc_clicked", metaCustom: "RKAtcClicked" },
  // Wave 3: consolidated finish screen + its paid add-on cards.
  finish_shown: { ga4: "rk_finish_shown", metaCustom: "RKFinishShown" },
  finish_addon_view: {
    ga4: "rk_finish_addon_view",
    metaCustom: "RKFinishAddonView",
  },
  finish_addon_accept: {
    ga4: "rk_finish_addon_accept",
    metaCustom: "RKFinishAddonAccept",
  },
  // Upload-later: the customer chose "I'll send it later" on the PDP.
  upload_later_chosen: {
    ga4: "rk_upload_later_chosen",
    metaCustom: "RKUploadLaterChosen",
  },
  // Pet-photo trap: detection + in-place Portrait upgrade outcome.
  photo_detected: { ga4: "rk_photo_detected", metaCustom: "RKPhotoDetected" },
  photo_upgrade_taken: {
    ga4: "rk_photo_upgrade_taken",
    metaCustom: "RKPhotoUpgradeTaken",
  },
  photo_upgrade_declined: {
    ga4: "rk_photo_upgrade_declined",
    metaCustom: "RKPhotoUpgradeDeclined",
  },
  // W4 phase 2: live on-garment photo preview reach + engagement.
  preview_photo_shown: {
    ga4: "rk_preview_photo_shown",
    metaCustom: "RKPreviewPhotoShown",
  },
  preview_dragged: {
    ga4: "rk_preview_dragged",
    metaCustom: "RKPreviewDragged",
  },
};

type TrackerWindow = {
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
};

// Flatten the detail object into snake_case GA4 event params, dropping
// anything undefined so the report doesn't fill with empty columns.
function ga4Params(detail: AnalyticsEventDetail): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (detail.templateName) p.template_name = detail.templateName;
  if (detail.templateTag) p.template_tag = detail.templateTag;
  if (detail.productHandle) p.product_handle = detail.productHandle;
  if (detail.placementSlug) p.placement_slug = detail.placementSlug;
  if (detail.mode) p.mode = detail.mode;
  if (typeof detail.spotCount === "number") p.spot_count = detail.spotCount;
  if (typeof detail.spotIndex === "number") p.spot_index = detail.spotIndex;
  if (detail.upsellKind) p.upsell_kind = detail.upsellKind;
  if (detail.upsellLabel) p.upsell_label = detail.upsellLabel;
  if (typeof detail.upsellPriceCents === "number") {
    p.upsell_value = detail.upsellPriceCents / 100;
  }
  if (detail.approvalChoice) p.approval_choice = detail.approvalChoice;
  if (detail.flowKind) p.flow_kind = detail.flowKind;
  if (detail.addonKind) p.addon_kind = detail.addonKind;
  if (typeof detail.uploadMs === "number") p.upload_ms = Math.round(detail.uploadMs);
  if (typeof detail.priceCents === "number") p.price_cents = detail.priceCents;
  if (typeof detail.totalCents === "number") {
    p.value = detail.totalCents / 100;
    p.currency = detail.currency || "AUD";
  }
  return p;
}

// Meta event params. Meta likes a flatter shape than GA4.
function metaParams(detail: AnalyticsEventDetail): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (detail.placementSlug) p.placement = detail.placementSlug;
  if (detail.templateTag) p.template = detail.templateTag;
  if (detail.upsellLabel) p.content_name = detail.upsellLabel;
  if (typeof detail.spotCount === "number") p.num_items = detail.spotCount;
  if (typeof detail.totalCents === "number") {
    p.value = detail.totalCents / 100;
    p.currency = detail.currency || "AUD";
  } else if (typeof detail.upsellPriceCents === "number") {
    p.value = detail.upsellPriceCents / 100;
    p.currency = detail.currency || "AUD";
  }
  return p;
}

function forwardToTrackers(
  name: AnalyticsEventName,
  detail: AnalyticsEventDetail,
): void {
  if (typeof window === "undefined") return;
  const map = EVENT_MAP[name];
  const w = window as unknown as TrackerWindow;

  // GA4. gtag routes to every configured property, so we pin send_to to
  // the RK Threads measurement ID.
  try {
    if (typeof w.gtag === "function") {
      w.gtag("event", map.ga4, {
        send_to: GA4_MEASUREMENT_ID,
        ...ga4Params(detail),
      });
    }
  } catch {
    // best-effort
  }

  // Meta Pixel. trackSingle / trackSingleCustom pin the event to the RK
  // Threads pixel so it isn't double-counted by other pixels on the page.
  try {
    if (typeof w.fbq === "function") {
      const params = metaParams(detail);
      if (map.metaStandard) {
        w.fbq("trackSingle", META_PIXEL_ID, map.metaStandard, params);
      } else if (map.metaCustom) {
        w.fbq("trackSingleCustom", META_PIXEL_ID, map.metaCustom, params);
      }
    }
  } catch {
    // best-effort
  }

  // Klaviyo onsite (best-effort): link the profile cookie to the known
  // email so abandoned-design recovery + Klaviyo's own browse/cart flows
  // can reach this customer. We only IDENTIFY here; the authoritative
  // "Started Custom Design" event is sent SERVER-side from App.tsx so it
  // survives ad-blockers (and this user's pixel-blocking browser). Identify
  // is idempotent, so firing it on every email-bearing event is harmless.
  try {
    const kw = window as unknown as {
      klaviyo?: { push: (a: unknown[]) => void };
    };
    if (detail.customerEmail && typeof kw.klaviyo?.push === "function") {
      kw.klaviyo.push(["identify", { $email: detail.customerEmail }]);
    }
  } catch {
    // best-effort
  }
}

// Wave 1: STANDARD GA4 ecommerce add_to_cart, fired alongside the custom
// add_to_cart_success event so GA4's built-in ecommerce reports (and any
// audience built on add_to_cart) see the customiser conversion too. Same
// best-effort + send_to pinning as forwardToTrackers. Meta is untouched
// here - the theme's own AddToCart pixel already covers the standard event.
export function emitGa4AddToCart(item: {
  variantId: string | null | undefined;
  itemName?: string | null;
  priceCents?: number | null;
  currency?: string;
}): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as TrackerWindow;
  if (typeof w.gtag !== "function") return;
  const price =
    typeof item.priceCents === "number" ? item.priceCents / 100 : undefined;
  try {
    w.gtag("event", "add_to_cart", {
      send_to: GA4_MEASUREMENT_ID,
      currency: item.currency || "AUD",
      ...(typeof price === "number" ? { value: price } : {}),
      items: [
        {
          // ctx.variantId is usually the bare numeric id already; the
          // split covers a gid://shopify/ProductVariant/... shape too.
          item_id: String(item.variantId ?? "").split("/").pop() || "unknown",
          ...(item.itemName ? { item_name: item.itemName } : {}),
          ...(typeof price === "number" ? { price } : {}),
          quantity: 1,
        },
      ],
    });
  } catch {
    // best-effort
  }
}

export function emitAnalytics(
  name: AnalyticsEventName,
  detail: AnalyticsEventDetail,
): void {
  // 1. DOM event for any theme / app listener.
  if (typeof document !== "undefined") {
    try {
      document.dispatchEvent(
        new CustomEvent(`rk-pc:${name}`, { detail, bubbles: true }),
      );
    } catch {
      // Some older browsers throw on CustomEvent in edge cases. Safe to
      // swallow — analytics is best-effort.
    }
  }
  // 2. Forward to GA4 + Meta Pixel.
  forwardToTrackers(name, detail);
}
